import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Clock, AlertTriangle, Bed } from 'lucide-react';
import EDDashboardV3 from './components/EDDashboardV3';
import BedManagementDashboard from './components/BedManagementDashboard';
import { BedManagementProvider } from './contexts/FirestoreBedManagementContext';
import { FirebaseAuthGate } from './components/FirebaseAuthGate';
import { EmailLinkConfirmation } from './components/EmailLinkConfirmation';
import { createAuditLog, AUDIT_ACTIONS } from './services/audit.service';
import * as healthcareApi from './services/healthcareApi.service';
import FhirApiTest from './components/FhirApiTest';

function App() {
  const [currentView, setCurrentView] = useState('clinical-workstation');
  const [user, setUser] = useState({ userId: 'demo@thegoatnote.com', role: 'physician' });
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPatients, setTotalPatients] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showBedManagement, setShowBedManagement] = useState(false);
  const [edPatients, setEdPatients] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [patientsPerPage, setPatientsPerPage] = useState(20);
  
  // Feature flag to use Healthcare API
  const USE_HEALTHCARE_API = process.env.REACT_APP_USE_HEALTHCARE_API === 'true';
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://goatnote-ehr-backend-233677712628.us-central1.run.app';

  // Load patients on component mount
  useEffect(() => {
    fetchPatients('', 1, 20);
  }, []);

  // Function to fetch patients
  const fetchPatients = async (search = '', page = 1, limit = 20) => {
    setLoading(true);
    try {
      if (USE_HEALTHCARE_API) {
        // Use Healthcare API
        console.log('Fetching patients from Healthcare API...');
        const data = await healthcareApi.searchPatients(search, page, limit);
        console.log('Healthcare API data:', data);
        
        setPatients(data.patients || []);
        setTotalPatients(data.total || 0);
        
        // Filter ED patients
        const edFiltered = (data.patients || []).filter(patient => 
          patient.activeEncounter?.status === 'in-progress' &&
          patient.activeEncounter?.class === 'emergency'
        );
        setEdPatients(edFiltered);
      } else {
        // Use existing backend API
        let url = `${API_BASE_URL}/api/patients?page=${page}&limit=${limit}`;
        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }
        
        console.log('Fetching patients from:', url);
        const response = await fetch(url);
        console.log('Response status:', response.status);
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Response error:', errorText);
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log('Patients data:', data);
        
        setPatients(data.patients || []);
        setTotalPatients(data.total || 0);
        
        // Filter ED patients
        const edFiltered = (data.patients || []).filter(patient => 
          patient.activeEncounter?.status === 'in-progress' &&
          patient.activeEncounter?.class === 'emergency'
        );
        setEdPatients(edFiltered);
      }
      
      // Audit log patient list access
      await createAuditLog(AUDIT_ACTIONS.VIEW_PATIENT_LIST, 'patients/list', {
        search,
        page,
        limit,
        totalResults: totalPatients,
        dataSource: USE_HEALTHCARE_API ? 'healthcare-api' : 'backend-api',
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error fetching patients:', error);
      
      // Only use mock data if Healthcare API is not enabled
      if (!USE_HEALTHCARE_API) {
        // Temporary mock data for testing
        const mockPatients = [
          {
            id: 'mock-1',
            name: 'John Doe',
            birthDate: '1980-01-01',
            age: 44,
            gender: 'Male',
            mrn: 'MRN001',
            activeEncounter: {
              status: 'in-progress',
              class: 'emergency',
              esiLevel: 2,
              reasonForVisit: 'Chest pain'
            }
          },
          {
            id: 'mock-2',
            name: 'Jane Smith',
            birthDate: '1975-05-15',
            age: 49,
            gender: 'Female',
            mrn: 'MRN002',
            activeEncounter: {
              status: 'in-progress',
              class: 'emergency',
              esiLevel: 3,
              reasonForVisit: 'Abdominal pain'
            }
          }
        ];
        
        // Use mock data if API fails
        console.warn('Using mock patient data due to API failure');
        setPatients(mockPatients);
        setTotalPatients(mockPatients.length);
        
        // Filter ED patients from mock data
        const edFiltered = mockPatients.filter(patient => 
          patient.activeEncounter?.status === 'in-progress' &&
          patient.activeEncounter?.class === 'emergency'
        );
        setEdPatients(edFiltered);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = async (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
    
    // Audit log search action
    if (term) {
      await createAuditLog(AUDIT_ACTIONS.SEARCH_PATIENTS, 'patients/search', {
        searchTerm: term,
        timestamp: new Date().toISOString()
      });
    }
    
    fetchPatients(term, 1, patientsPerPage);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchPatients(searchTerm, newPage, patientsPerPage);
  };

  const handlePatientsPerPageChange = (newLimit) => {
    setPatientsPerPage(newLimit);
    setCurrentPage(1);
    fetchPatients(searchTerm, 1, newLimit);
  };

  // Handle patient selection
  const handlePatientSelect = async (patient) => {
    setSelectedPatient(patient);
    setCurrentView('clinical-workstation');
    
    // Audit log patient view
    await createAuditLog(AUDIT_ACTIONS.VIEW_PATIENT, `patient/${patient.id}`, {
      patientId: patient.id,
      patientName: patient.name,
      patientMRN: patient.mrn || patient.id,
      esiLevel: patient.activeEncounter?.esiLevel,
      chiefComplaint: patient.activeEncounter?.reasonForVisit,
      accessReason: 'routine_care',
      timestamp: new Date().toISOString()
    });
  };

  // Handle view patient list
  const handleViewPatientList = async () => {
    setCurrentView('patient-list');
    setShowBedManagement(false);
    
    // Audit log navigation to patient list
    await createAuditLog(AUDIT_ACTIONS.VIEW_PATIENT_LIST, 'navigation/patient-list', {
      fromView: 'clinical-workstation',
      timestamp: new Date().toISOString()
    });
  };

  // Main app content component
  const MainApp = () => (
    <div className="min-h-screen bg-gray-100">
        <Routes>
          <Route path="/auth/confirm" element={<EmailLinkConfirmation />} />
          <Route path="/fhir-test" element={<FhirApiTest />} />
          <Route
            path="*"
            element={
              <FirebaseAuthGate>
                <BedManagementProvider>
                  <div className="App">
                    {USE_HEALTHCARE_API && (
                      <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4">
                        <p className="font-bold">Healthcare API Mode Active</p>
                        <p>Connected to Google Healthcare API with {totalPatients} patients</p>
                      </div>
                    )}
                    
                    {currentView === 'clinical-workstation' && (
                      <EDDashboardV3 
                        patient={selectedPatient}
                        patients={patients}
                        totalPatients={totalPatients}
                        onPatientSelect={handlePatientSelect}
                        onViewPatientList={handleViewPatientList}
                        onLogout={() => {
                          // Logout will be handled by FirebaseAuthGate
                          // Remove localStorage items and reload
                          window.location.reload();
                        }}
                        user={user}
                        apiBase={API_BASE_URL}
                        isMainWorkstation={true}
                        onRefreshPatients={() => fetchPatients(searchTerm, currentPage, patientsPerPage)}
                        loading={loading}
                      />
                    )}

                    {currentView === 'patient-list' && (
                      showBedManagement ? (
                        <BedManagementDashboard edPatients={edPatients} />
                      ) : (
                        <div className="min-h-screen bg-gray-100">
                          {/* Patient List Header */}
                          <div className="bg-white shadow-md">
                            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                              <div className="py-4 flex justify-between items-center">
                                <div className="flex items-center space-x-4">
                                  <h1 className="text-2xl font-bold text-gray-900">Patient List</h1>
                                  <span className="text-sm text-gray-500">
                                    {edPatients.length} Active ED | {totalPatients} Total Patients
                                  </span>
                                </div>
                                <div className="flex space-x-3">
                                  <button
                                    onClick={() => setShowBedManagement(!showBedManagement)}
                                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                  >
                                    <Bed className="h-4 w-4 mr-2" />
                                    {showBedManagement ? 'Patient List' : 'Bed Management'}
                                  </button>
                                  <button
                                    onClick={() => setCurrentView('clinical-workstation')}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                                  >
                                    Back to Dashboard
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Search Bar */}
                          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                            <input
                              type="text"
                              placeholder="Search patients by name, MRN, or condition..."
                              value={searchTerm}
                              onChange={(e) => handleSearch(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>

                          {/* Patient Table */}
                          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="bg-white shadow rounded-lg overflow-hidden">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Patient
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      MRN
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      ESI Level
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Chief Complaint
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Actions
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {loading ? (
                                    <tr>
                                      <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                                        Loading patients...
                                      </td>
                                    </tr>
                                  ) : patients.length === 0 ? (
                                    <tr>
                                      <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                                        No patients found
                                      </td>
                                    </tr>
                                  ) : (
                                    patients.map((patient) => (
                                      <tr key={patient.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                          <div className="text-sm font-medium text-gray-900">
                                            {patient.name}
                                          </div>
                                          <div className="text-sm text-gray-500">
                                            {patient.birthDate} ({patient.age}y {patient.gender})
                                          </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                          {patient.mrn || patient.id}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                          {patient.activeEncounter?.esiLevel && (
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                              patient.activeEncounter.esiLevel === 1 ? 'bg-red-100 text-red-800' :
                                              patient.activeEncounter.esiLevel === 2 ? 'bg-orange-100 text-orange-800' :
                                              patient.activeEncounter.esiLevel === 3 ? 'bg-yellow-100 text-yellow-800' :
                                              patient.activeEncounter.esiLevel === 4 ? 'bg-green-100 text-green-800' :
                                              'bg-blue-100 text-blue-800'
                                            }`}>
                                              ESI-{patient.activeEncounter.esiLevel}
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900">
                                          {patient.activeEncounter?.reasonForVisit || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                          <button
                                            onClick={() => handlePatientSelect(patient)}
                                            className="text-blue-600 hover:text-blue-900"
                                          >
                                            View Details
                                          </button>
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>

                              {/* Pagination */}
                              {totalPatients > 0 && (
                                <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                                  <div className="flex-1 flex justify-between sm:hidden">
                                    <button
                                      onClick={() => handlePageChange(currentPage - 1)}
                                      disabled={currentPage === 1}
                                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                      Previous
                                    </button>
                                    <button
                                      onClick={() => handlePageChange(currentPage + 1)}
                                      disabled={currentPage >= Math.ceil(totalPatients / patientsPerPage)}
                                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                      Next
                                    </button>
                                  </div>
                                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                    <div>
                                      <p className="text-sm text-gray-700">
                                        Showing{' '}
                                        <span className="font-medium">{(currentPage - 1) * patientsPerPage + 1}</span>
                                        {' '}to{' '}
                                        <span className="font-medium">
                                          {Math.min(currentPage * patientsPerPage, totalPatients)}
                                        </span>
                                        {' '}of{' '}
                                        <span className="font-medium">{totalPatients}</span>
                                        {' '}results
                                      </p>
                                    </div>
                                    <div className="flex items-center space-x-4">
                                      <select
                                        value={patientsPerPage}
                                        onChange={(e) => handlePatientsPerPageChange(Number(e.target.value))}
                                        className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                                      >
                                        <option value={20}>20 per page</option>
                                        <option value={25}>25 per page</option>
                                        <option value={50}>50 per page</option>
                                      </select>
                                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                        <button
                                          onClick={() => handlePageChange(1)}
                                          disabled={currentPage === 1}
                                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                                        >
                                          First
                                        </button>
                                        <button
                                          onClick={() => handlePageChange(currentPage - 1)}
                                          disabled={currentPage === 1}
                                          className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                                        >
                                          Previous
                                        </button>
                                        <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                          Page {currentPage} of {Math.ceil(totalPatients / patientsPerPage)}
                                        </span>
                                        <button
                                          onClick={() => handlePageChange(currentPage + 1)}
                                          disabled={currentPage >= Math.ceil(totalPatients / patientsPerPage)}
                                          className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                                        >
                                          Next
                                        </button>
                                        <button
                                          onClick={() => handlePageChange(Math.ceil(totalPatients / patientsPerPage))}
                                          disabled={currentPage >= Math.ceil(totalPatients / patientsPerPage)}
                                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                                        >
                                          Last
                                        </button>
                                      </nav>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </BedManagementProvider>
              </FirebaseAuthGate>
            }
          />
        </Routes>
    </div>
  );

  // Render the main application with routing
  return (
    <Router>
      <Routes>
        <Route path="/confirm" element={<EmailLinkConfirmation />} />
        <Route path="/" element={<MainApp />} />
        {/* <Route path="/" element={<MainApp />} /> */}
        {/* Temporarily bypass auth for testing - REMOVE IN PRODUCTION */}
        {/* <Route path="/" element={
          <BedManagementProvider>
            <div className="App">
              <h1>Auth Bypassed - Development Mode</h1>
              <p>This bypasses authentication for testing only!</p>
            </div>
          </BedManagementProvider>
        } /> */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
